import { useCallback } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useApiQuery } from "../../../../common/infrastructure/hooks/useApiQuery";
import { useApiMutation } from "../../../../common/infrastructure/hooks/useApiMutation";
import { endpointsMetadata } from "../../../../common/infrastructure/routes/endpointsMetadata";
import { useAppContext } from "../../../../AppContext";
import { showCustomConfirmDialog } from "../../../../common/components/dialogs/AppConfirmDialog";
import { IExpiringBatchModel } from "../models/inventoryOverviewModels";

export const useExpirationTab = (canDestroy: boolean) => {
  const { selectedStudioId, currentUser } = useAppContext();
  const queryClient = useQueryClient();

  const queryKey = [endpointsMetadata.inventoryOverview.getExpiringBatches, selectedStudioId];

  const { data: batches = [], isLoading } = useApiQuery<IExpiringBatchModel[]>({
    queryKey,
    endpoint: endpointsMetadata.inventoryOverview.getExpiringBatches,
    enabled: selectedStudioId > 0,
    globalIsLoading: false,
  });

  const { mutate } = useApiMutation({
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
      queryClient.invalidateQueries({
        queryKey: [endpointsMetadata.inventoryOverview.getProductsByQuantity, selectedStudioId],
      });
      queryClient.invalidateQueries({
        queryKey: [endpointsMetadata.inventoryItemNomenclature.getAllCategorized, selectedStudioId],
      });
    },
  });

  const destroyBatch = useCallback(
    (batch: IExpiringBatchModel) => {
      if (!canDestroy || batch.daysUntilExpiry >= 0) return;

      const fullName = [currentUser?.firstName, currentUser?.lastName]
        .filter(Boolean)
        .join(" ")
        .trim();
      const displayName = fullName || currentUser?.email || "Неизвестен";
      const qty = batch.remainingQuantity;
      const newQuantity = batch.currentQuantity - qty;

      const message = `Унищожаване на <b>${qty} бр. ${batch.productName}</b> с изтекъл срок?<br/>Наличността ще бъде намалена от ${batch.currentQuantity} на ${newQuantity}.`;

      showCustomConfirmDialog(
        message,
        () => {
          mutate({
            endpoint: endpointsMetadata.inventoryItemNomenclature.adjustQuantity,
            method: "put",
            payload: {
              itemId: batch.inventoryItemId,
              newQuantity,
              reason: `Потребител ${displayName} унищожи ${qty} бр. ${batch.productName} от страницата за наличности`,
            },
            invalidateKeys: [
              queryKey,
              [endpointsMetadata.inventoryOverview.getProductsByQuantity, selectedStudioId],
              [endpointsMetadata.inventoryItemNomenclature.getAllCategorized, selectedStudioId],
            ],
          });
        },
        undefined,
        "warning",
        "Унищожи",
        "Отказ",
      );
    },
    [canDestroy, currentUser, mutate, queryKey, selectedStudioId],
  );

  return { batches, isLoading, destroyBatch };
};
